/**
 * overlayLevel.js — сколько текста показывать поверх снимка.
 *
 * ПОВОД. Снимок экрана врача: столбец подписей занимает четверть аксиального
 * окна и лежит прямо на изображении — имя, исследование, аппарат, Ex/Se/Im,
 * плоскость, срез, толщина, протокол, TR/TE/TI/FA/BW/NEX/ET, размер вокселя,
 * катушка, число снимков, дата, W/L, масштаб, инструмент, ID, дата рождения.
 * Всё это нужно — но не всё сразу и не всегда.
 *
 * ТРИ СОСТОЯНИЯ, А НЕ ДВА. Было два: «обычный» и «Инфо: подробно». Ни одно из
 * них не убирает текст со снимка совсем, а именно это и попросили. Третьего —
 * «скрыто» — не хватало. Заодно «кратко» полезнее прежнего «обычного»: в нём
 * остаётся ровно то, без чего смотреть нельзя.
 *
 * ЧТО ОСТАЁТСЯ В «КРАТКО» И ПОЧЕМУ ИМЕННО ЭТО:
 *   · пациент   — чтобы нельзя было молча смотреть чужое исследование;
 *   · плоскость — реформаты легко перепутать, подпись стоит копейки;
 *   · срез N/M  — единственное, что меняется при прокрутке;
 *   · толщина   — от неё зависит, что вообще видно на срезе.
 *
 * Файл без импортов: правило проверяется тестом, а не разглядыванием экрана.
 */

/** Порядок обхода по кнопке и горячей клавише. */
export const УРОВНИ = Object.freeze(['подробно', 'кратко', 'скрыто']);

/** Поля, которые видны в «кратко». Значения атрибута data-ov. */
export const КРАТКИЕ_ПОЛЯ = Object.freeze(['patient', 'plane', 'slice', 'thickness']);

export const КЛЮЧ_ХРАНИЛИЩА = 'ui.overlayLevel';

const ПОДПИСИ = Object.freeze({
  подробно: 'Инфо: подробно',
  кратко: 'Инфо: кратко',
  скрыто: 'Инфо: скрыто',
});

const ЗАГОЛОВКИ = Object.freeze({
  подробно: 'Все параметры съёмки поверх снимка — как у станционного вьювера. Нажмите (или клавиша H), чтобы сократить',
  кратко: 'Только пациент, плоскость, срез и толщина. Нажмите (или клавиша H), чтобы убрать совсем',
  скрыто: 'Подписи убраны — чистое изображение. Нажмите (или клавиша H), чтобы вернуть подробные',
});

/** Привести что угодно к допустимому уровню. */
export function нормализовать(уровень) {
  return УРОВНИ.includes(уровень) ? уровень : 'кратко';
}

/** Следующий уровень по кругу: подробно → кратко → скрыто → подробно. */
export function следующий(уровень) {
  const i = УРОВНИ.indexOf(нормализовать(уровень));
  return УРОВНИ[(i + 1) % УРОВНИ.length];
}

/**
 * Классы на <body> для уровня. Пусто для «подробно» — это состояние по
 * умолчанию у самих правил (.ov-rich показываются классом overlay-rich).
 */
export function классыУровня(уровень) {
  switch (нормализовать(уровень)) {
    case 'подробно': return ['overlay-rich'];
    case 'кратко': return ['overlay-brief'];
    default: return ['overlay-hidden'];
  }
}

/** Все классы, которыми управляет этот модуль, — чтобы снимать чужие. */
export const ВСЕ_КЛАССЫ = Object.freeze(['overlay-rich', 'overlay-brief', 'overlay-hidden']);

/** Подпись на кнопке и пояснение к ней. */
export function подпись(уровень) { return ПОДПИСИ[нормализовать(уровень)]; }
export function пояснение(уровень) { return ЗАГОЛОВКИ[нормализовать(уровень)]; }

/**
 * Что прочитать из хранилища при запуске.
 *
 * Старый выключатель «подробно вкл/выкл» (ui.overlayRich) переводится в новые
 * уровни: включено — «подробно», выключено — «кратко». Терять чужую настройку
 * при обновлении нельзя: врач один раз её поставил и забыл.
 */
export function уровеньИзХранилища(читать) {
  const прямо = читать(КЛЮЧ_ХРАНИЛИЩА);
  if (УРОВНИ.includes(прямо)) return прямо;
  const старый = читать('ui.overlayRich');
  if (старый === '0') return 'кратко';
  if (старый === '1') return 'подробно';
  return 'кратко';   // по умолчанию — тихо: меньше текста поверх снимка
}

export default {
  УРОВНИ, КРАТКИЕ_ПОЛЯ, КЛЮЧ_ХРАНИЛИЩА, ВСЕ_КЛАССЫ,
  нормализовать, следующий, классыУровня, подпись, пояснение, уровеньИзХранилища,
};
